/**
 * MessageContextMenu — Rechtsklick / Long-Press Menü für Nachrichten
 *
 * Optionen: Antworten, Reagieren, Kopieren, Bearbeiten (eigene), Löschen (eigene)
 */

import { useEffect, useRef, type ReactNode } from "react";
import styles from "./MessageContextMenu.module.css";

export interface ContextMenuAction {
  label: string;
  icon: ReactNode;
  action: () => void;
  danger?: boolean;
}

interface MessageContextMenuProps {
  x: number;
  y: number;
  actions: ContextMenuAction[];
  onClose: () => void;
}

export function MessageContextMenu({ x, y, actions, onClose }: MessageContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleScroll = () => onClose();

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [onClose]);

  // Adjust position to keep menu visible
  useEffect(() => {
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const viewW = window.innerWidth;
    const viewH = window.innerHeight;

    if (rect.right > viewW) {
      menuRef.current.style.left = `${x - rect.width}px`;
    }
    if (rect.bottom > viewH) {
      menuRef.current.style.top = `${y - rect.height}px`;
    }
  }, [x, y]);

  return (
    <div
      ref={menuRef}
      className={styles.menu}
      style={{ top: y, left: x }}
    >
      {actions.map((a) => (
        <button
          key={a.label}
          className={`${styles.item} ${a.danger ? styles.danger : ""}`}
          onClick={() => {
            a.action();
            onClose();
          }}
        >
          <span className={styles.icon}>{a.icon}</span>
          <span className={styles.label}>{a.label}</span>
        </button>
      ))}
    </div>
  );
}
